import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Input } from 'reactstrap';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Icon } from 'js/components';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import GroupingAnswerChip from './GroupingAnswerChip';
import GroupingDropZone from './GroupingDropZone';
import {
  ANSWER_DRAG_TYPE,
  answersInGroup,
  canRenameGroup,
  moveAnswerToGroup,
  nextGroupName,
  removeGroup,
  renameGroup,
  unassignedAnswers,
} from './grouping-utils';
import styles from './Grouping.module.scss';

export default function CustomGrouping({
  answers = [],
  groups = {},
  onChange,
  disabled = false,
}) {
  const unassigned = useMemo(
    () => unassignedAnswers(answers, groups),
    [answers, groups]
  );
  const groupNames = useMemo(() => Object.keys(groups), [groups]);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const updateGroups = useCallback(
    (next) => {
      onChange?.(next);
    },
    [onChange]
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data?.type === ANSWER_DRAG_TYPE,
      onDrop: ({ source, location }) => {
        const target = location.current.dropTargets[0];
        if (!target || disabled) {
          return;
        }
        const value = source.data?.value;
        if (value == null) {
          return;
        }
        const destType = target.data?.type;
        const destName =
          destType === 'breakout-group' ? target.data.groupName : null;
        updateGroups(moveAnswerToGroup(groupsRef.current, value, destName));
      },
    });
  }, [disabled, updateGroups]);

  const handleAddGroup = () => {
    const name = nextGroupName(groups);
    updateGroups({ ...groups, [name]: [] });
  };

  const handleRename = (oldName, newName, input) => {
    const trimmed = String(newName ?? '').trim();
    if (trimmed === oldName) {
      if (input) {
        input.value = oldName;
      }
      return;
    }
    const check = canRenameGroup(groups, oldName, newName);
    if (!check.ok) {
      if (input) {
        input.value = oldName;
      }
      return;
    }
    updateGroups(renameGroup(groups, oldName, newName));
  };

  const handleDelete = (name) => {
    updateGroups(removeGroup(groups, name));
  };

  return (
    <div className={styles.customLayout}>
      <section className={styles.unassigned}>
        <h4 className={styles.sectionLabel}>Ungrouped answers</h4>
        <GroupingDropZone
          targetType="breakout-unassigned"
          hasItems={unassigned.length > 0}
          emptyLabel="Drag answers here to leave them ungrouped"
        >
          <ul className={styles.chipList}>
            {unassigned.map((answer) => (
              <GroupingAnswerChip
                key={answer.id ?? answer.value}
                answer={answer}
                draggable={!disabled}
              />
            ))}
          </ul>
        </GroupingDropZone>
      </section>

      {groupNames.length > 0 ? (
        <ul className={styles.groupList}>
          {groupNames.map((name) => {
            const groupedAnswers = answersInGroup(answers, groups[name]);
            return (
              <li key={name} className={styles.group}>
                <div className={styles.groupHeader}>
                  <Input
                    className={styles.groupName}
                    defaultValue={name}
                    disabled={disabled}
                    onBlur={(e) => handleRename(name, e.target.value, e.target)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    aria-label="Cohort name"
                  />
                  <Button
                    type="button"
                    className="btn-action-alt"
                    color="secondary"
                    onClick={() => handleDelete(name)}
                    disabled={disabled}
                    aria-label={`Delete cohort ${name}`}
                  >
                    <Icon icon={faTrashAlt} className="icon" />
                  </Button>
                </div>
                <GroupingDropZone
                  targetType="breakout-group"
                  groupName={name}
                  hasItems={groupedAnswers.length > 0}
                  emptyLabel="Drag answers into this cohort"
                >
                  <ul className={styles.chipList}>
                    {groupedAnswers.map((answer) => (
                      <GroupingAnswerChip
                        key={answer.id ?? answer.value}
                        answer={answer}
                        draggable={!disabled}
                      />
                    ))}
                  </ul>
                </GroupingDropZone>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div>
        <Button
          type="button"
          color="link"
          className={styles.addGroup}
          onClick={handleAddGroup}
          disabled={disabled}
        >
          <Icon icon={faPlus} className="mr-1" />
          Add cohort
        </Button>
      </div>
    </div>
  );
}
